import React, { memo, useMemo } from "react"

import {
  CRITERION_VALUE,
  type CriterionValue,
} from "@/constants/criterion-values"
import { EVENTS, useEventDispatcher } from "@/inspection-events"
import { yesNoBadgeShapeClasses } from "@/metrics/metric-display"
import type { MetricReviewTranscriptContext } from "@/metrics/runtime"
import { useUser } from "@clerk/nextjs"
import {
  Badge,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@coval/ui"
import { Users } from "lucide-react"

import type { Annotation } from "@/types/api/Annotation"
import type { RunMetricOutput } from "@/types/RunMetricOutput"
import { cn } from "@/utils/cn"

import { useCriterionVoting } from "../../hooks/useCriterionVoting"
import {
  COMPOSITE_AI_COLUMN_CLASS,
  COMPOSITE_HUMAN_COLUMN_CLASS,
  CRITERION_DISPLAY,
  CRITERION_VALUES,
  getCriterionBadgeClass,
  getCriterionSelectedButtonClass,
  type CriterionResult,
} from "./composite-evaluation-types"
import { MessageReferenceChip } from "./MessageReferenceChip"

interface CompositeCriteriaReviewProps {
  metricId: string
  simulationOutputId: string
  annotationsForMetric: Annotation[]
  metricOutput?: RunMetricOutput
  metadataCriteria?: string[] | null
  reportingMethod: string
  projectId?: string
  /** Blind review hides the machine verdict so the reviewer isn't anchored. */
  showAiResult?: boolean
  /** The conversation the criteria were judged against, for naming and
   *  previewing the messages each criterion cites. */
  transcriptContext?: MetricReviewTranscriptContext
}

/** The machine verdict reads as a full phrase; the vote buttons use the short
 *  `CRITERION_DISPLAY` labels so three of them fit on one row. */
const AI_RESULT_LABEL: Record<CriterionValue, string> = {
  [CRITERION_VALUE.MET]: "Met",
  [CRITERION_VALUE.NOT_MET]: "Not met",
  [CRITERION_VALUE.UNKNOWN]: "N/A",
}

/** Criterion text plus the AI's reasoning for it, in full: the reasoning is
 *  what a reviewer weighs their own vote against, so it is never cut off. */
const CriterionCell: React.FC<{
  result: CriterionResult
  showExplanation: boolean
  transcriptContext?: MetricReviewTranscriptContext
  onMessageSelect: (messageIndex: number) => void
  onReferencesHoverChange: (messageIndices: number[] | null) => void
}> = ({
  result,
  showExplanation,
  transcriptContext,
  onMessageSelect,
  onReferencesHoverChange,
}) => {
  const messageIndices = result.matching_message_indices ?? []

  return (
    <div className="space-y-1">
      <p className="text-sm font-semibold leading-snug text-coval-text wrap-break-word">
        {result.criterion}
      </p>
      {showExplanation && result.explanation && (
        <div className="space-y-2 text-sm">
          <div className="whitespace-pre-wrap wrap-break-word text-coval-text-muted">
            {result.explanation}
          </div>
          {messageIndices.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm">
              <span className="text-coval-text-muted">Referenced:</span>
              {messageIndices.map((msgIndex, referenceIndex) => (
                <React.Fragment key={msgIndex}>
                  {referenceIndex > 0 && (
                    <span aria-hidden className="text-coval-text-muted">
                      ·
                    </span>
                  )}
                  <MessageReferenceChip
                    message={transcriptContext?.messages[msgIndex]}
                    messageIndex={msgIndex}
                    participants={transcriptContext?.participants}
                    onSelect={onMessageSelect}
                    // Pointing at one of a criterion's messages lifts the whole
                    // set out of the transcript, so the reviewer sees the
                    // evidence as the criterion cites it rather than one turn
                    // of it at a time.
                    onPreviewOpenChange={(isOpen) =>
                      onReferencesHoverChange(isOpen ? messageIndices : null)
                    }
                  />
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Per-criterion human review: one row per criterion, pairing the AI's verdict
 * and reasoning with the reviewer's own vote. Stating each criterion once
 * removes the separate results table that repeated them all.
 */
export const CompositeCriteriaReview: React.FC<CompositeCriteriaReviewProps> =
  memo(
    ({
      metricId,
      simulationOutputId,
      annotationsForMetric,
      metricOutput,
      metadataCriteria,
      reportingMethod,
      projectId,
      showAiResult = true,
      transcriptContext,
    }) => {
      const { user } = useUser()
      const userEmail = user?.emailAddresses?.[0]?.emailAddress
      const dispatch = useEventDispatcher()

      const { criteria, selectedValues, handleCriterionChange } =
        useCriterionVoting({
          metricId,
          simulationOutputId,
          annotationsForMetric,
          metricOutput,
          metadataCriteria,
          reportingMethod,
          projectId,
          gateScoreUntilAllVoted: true,
        })

      const otherAnnotations = useMemo(
        () => annotationsForMetric.filter((a) => a.assignee !== userEmail),
        [annotationsForMetric, userEmail],
      )

      const handleMessageSelect = (messageIndex: number) => {
        dispatch(EVENTS.MESSAGE_SELECTED, { messageIndex })
      }

      // The transcript otherwise lifts every message the whole metric cites,
      // which on a metric with several criteria is most of the conversation.
      // Naming one criterion's set narrows that to the evidence under the
      // pointer, and clearing it hands the metric-wide view back.
      const handleReferencesHoverChange = (messageIndices: number[] | null) => {
        // While a reference is pointed at, the set travels without a metric id:
        // the viewer reads a hover that carries one as "highlight everything
        // this metric cites", which is the view being narrowed. On the way out
        // the id is what hands that wider view back — the card these references
        // sit in is still hovered and will not state it again by itself.
        dispatch(EVENTS.MESSAGES_HOVER, {
          messageIndices,
          ...(messageIndices ? {} : { metricId }),
        })
      }

      if (criteria.length === 0) {
        return null
      }

      return (
        <div className="w-full overflow-x-auto rounded-md border border-coval-border">
          <table className="w-full table-fixed">
            <thead>
              <tr className="border-b border-coval-border text-left text-sm">
                <th className="border-r border-coval-border px-3 py-2 font-semibold text-coval-text">
                  Criteria
                </th>
                {/* The AI's verdict sits between the criterion it judged and
                    the reviewer's own vote, so the two verdicts read side by
                    side. */}
                {showAiResult && (
                  <th
                    className={`${COMPOSITE_AI_COLUMN_CLASS} whitespace-nowrap border-r border-coval-border px-3 py-2 text-left font-semibold text-coval-text`}
                  >
                    AI
                  </th>
                )}
                <th
                  className={`${COMPOSITE_HUMAN_COLUMN_CLASS} whitespace-nowrap px-3 py-2 text-left font-semibold text-coval-text`}
                >
                  Human
                </th>
              </tr>
            </thead>
            <tbody>
              {criteria.map((criterion, index) => {
                const currentVal = (selectedValues?.[index] ?? "") as
                  | CriterionValue
                  | ""
                const aiLabel = AI_RESULT_LABEL[criterion.result]
                return (
                  <tr
                    key={index}
                    className="border-b border-coval-border align-top last:border-b-0"
                  >
                    <td className="border-r border-coval-border px-3 py-3">
                      <CriterionCell
                        result={criterion}
                        showExplanation={showAiResult}
                        transcriptContext={transcriptContext}
                        onMessageSelect={handleMessageSelect}
                        onReferencesHoverChange={handleReferencesHoverChange}
                      />
                    </td>
                    {showAiResult && (
                      <td
                        className={`${COMPOSITE_AI_COLUMN_CLASS} whitespace-nowrap border-r border-coval-border px-3 py-3`}
                      >
                        {aiLabel && (
                          <span
                            className={`${yesNoBadgeShapeClasses} ${getCriterionBadgeClass(criterion.result)}`}
                          >
                            {aiLabel}
                          </span>
                        )}
                      </td>
                    )}
                    <td className={`${COMPOSITE_HUMAN_COLUMN_CLASS} px-3 py-3`}>
                      {/* The vote buttons stack while the card is narrow, so
                          the Human column can stay slim enough to leave the
                          criteria text a readable measure. */}
                      <div className="flex flex-col items-stretch gap-1.5 @md/card:flex-row @md/card:items-start">
                        {CRITERION_VALUES.map((val) => (
                          <Button
                            key={val}
                            variant="secondary"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleCriterionChange(index, val)
                            }}
                            aria-label={`Mark criterion ${index + 1} as ${CRITERION_DISPLAY[val].label}`}
                            className={cn(
                              "w-full transition-colors duration-150 @md/card:w-auto",
                              currentVal === val
                                ? getCriterionSelectedButtonClass(val)
                                : // Unselected options keep the stock secondary
                                  // surface and border; once a vote exists the
                                  // others recede by label color, not opacity,
                                  // which would wash the border out too.
                                  currentVal && "text-coval-text-muted",
                            )}
                          >
                            {CRITERION_DISPLAY[val].label}
                          </Button>
                        ))}
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="secondary"
                              disabled={otherAnnotations.length === 0}
                              aria-label={`View other annotations for criterion ${index + 1}`}
                              className="w-full transition-all duration-200 @md/card:w-auto"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Users className="h-4 w-4" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent side="top" className="w-64 p-2">
                            <div className="space-y-1">
                              {otherAnnotations.length > 0 ? (
                                <div className="max-h-48 overflow-y-auto">
                                  {otherAnnotations.map((otherAnnotation) => {
                                    const otherVal =
                                      otherAnnotation.ground_truth_set_value?.[
                                        index
                                      ] ?? ""
                                    const display =
                                      CRITERION_DISPLAY[
                                        otherVal as CriterionValue
                                      ]
                                    return (
                                      <div
                                        key={otherAnnotation.id}
                                        className="flex items-center justify-between py-1 px-1"
                                      >
                                        <div className="flex items-center gap-2">
                                          {display && (
                                            <Badge
                                              variant={
                                                otherVal === CRITERION_VALUE.MET
                                                  ? "success"
                                                  : otherVal ===
                                                      CRITERION_VALUE.NOT_MET
                                                    ? "danger"
                                                    : "default"
                                              }
                                            >
                                              {display.label}
                                            </Badge>
                                          )}
                                          <span className="text-xs text-coval-text-muted">
                                            {otherAnnotation.assignee}
                                          </span>
                                        </div>
                                      </div>
                                    )
                                  })}
                                </div>
                              ) : (
                                <p className="text-sm text-coval-text-muted text-center py-4">
                                  No other user feedback available
                                </p>
                              )}
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )
    },
  )
CompositeCriteriaReview.displayName = "CompositeCriteriaReview"
